"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  Home, TrendingUp, LayoutGrid, Gift, UserPlus,
  Headset, Settings, Search, ChevronDown, LogOut, Crown,
  ArrowLeft, ChevronRight, Lock, Building2, Globe, LifeBuoy,
  Shield, FolderOpen, Sparkles, User,
} from "lucide-react";
import { useMembershipGate } from "./membership/MembershipGate";
import { FeatureLock } from "./membership/FeatureLock";
import { isPremiumOnly, premiumFeatureLabel } from "./membership/gate-config";
import { NotificationBell } from "./NotificationBell";

export type NavItem = { label: string; href: string; sub?: string; Icon: React.FC<React.SVGProps<SVGSVGElement>>; badge?: number; premium?: boolean };

// Desktop sidebar mirrors the CJ OWL mobile menu (bottom tabs + Menu screen), so
// both form factors expose the same items. Features the mobile app nests inside a
// hub — Value & Risk Guard (under Protection); Vault, Renewals & Subscriptions
// (under Organize); Rewards (under Membership/VIP) — are reached from those pages,
// not as separate sidebar rows.
const NAV: NavItem[] = [
  { label: "Dashboard",  href: "/my-account/",            sub: "Overview",                 Icon: Home },
  { label: "Savings",    href: "/my-account/savings/",    sub: "Reports & Opportunities",  Icon: TrendingUp, premium: true },
  { label: "Ask CJ OWL", href: "/my-account/ask/",        sub: "Your Savings Assistant",   Icon: Sparkles },
  { label: "Protection", href: "/my-account/protection/", sub: "Value & Risk Guard",       Icon: Shield },
  { label: "Organize",   href: "/my-account/organize/",   sub: "Vault, Renewals & Docs",   Icon: FolderOpen },
  { label: "Services",   href: "/my-account/services/",   sub: "All Categories",           Icon: LayoutGrid },
  { label: "Properties", href: "/my-account/properties/", sub: "Homes & Rentals",          Icon: Building2 },
  { label: "Membership", href: "/my-account/bills/",      sub: "Plan, Billing & Invoices", Icon: Crown },
  { label: "Referral",   href: "/my-account/referral/",   sub: "Invite & Track",           Icon: UserPlus, premium: true },
  { label: "Support",    href: "/my-account/concierge/",  sub: "Help & your Advisor",      Icon: LifeBuoy },
  { label: "My Profile", href: "/my-account/profile/",    sub: "Personal Details",         Icon: User },
  { label: "Settings",   href: "/my-account/settings/",   sub: "Preferences & Security",   Icon: Settings },
];

// Mobile bottom-tab set (matches the CJ OWL mobile app mockups). The center
// "Ask CJ OWL" tab is rendered separately (owl mascot), so this array holds the
// two left tabs then the two right tabs.
const BOTTOM: NavItem[] = [
  { label: "Home",       href: "/my-account/",            Icon: Home },
  { label: "Savings",    href: "/my-account/savings/",    Icon: TrendingUp, premium: true },
  { label: "Protection", href: "/my-account/protection/", Icon: Shield },
  { label: "Organize",   href: "/my-account/organize/",   Icon: FolderOpen },
];

/** Breadcrumb labels for the current /my-account/<segment> page. */
const SECTION_LABELS: Record<string, string> = {
  savings: "Savings",
  menu: "Menu",
  ask: "Ask CJ OWL",
  protection: "Protection",
  organize: "Organize",
  services: "Services",
  properties: "Properties",
  renewals: "Renewals",
  subscriptions: "Subscriptions",
  vault: "Vault",
  notifications: "Notifications",
  referral: "Referral",
  rewards: "Rewards",
  concierge: "Support",
  "value-risk-guard": "Value & Risk Guard",
  support: "Support",
  settings: "Settings",
  profile: "My Profile",
  password: "Change Password",
  bills: "Membership",
  documents: "Documents",
};

function initialsOf(name: string) {
  return name.split(/\s+/).map((n) => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "CJ";
}

function isActive(pathname: string, href: string) {
  if (href === "/my-account/") return pathname === "/my-account" || pathname === "/my-account/";
  const h = href.replace(/\/$/, "");
  return pathname === h || pathname.startsWith(h + "/");
}

function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <Link href="/my-account/" className="flex items-center">
      {/* Full CJ OWL badge (owl + wordmark + "by Comparison Junction") — same asset as the public nav. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/images/cj-owl/cj-owl-menu-logo-green.png"
        alt="CJ Owl"
        className={compact ? "h-9 w-auto" : "h-12 w-auto"}
      />
    </Link>
  );
}

function NavList({ pathname, locked, onNavigate }: { pathname: string; locked: boolean; onNavigate?: () => void }) {
  return (
    <nav className="space-y-1">
      {NAV.map((item) => {
        const active = isActive(pathname, item.href);
        const premiumLocked = locked && item.premium;
        return (
          <Link
            key={item.href}
            href={item.href}
            onClick={onNavigate}
            className={`group flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-colors ${
              active ? "bg-lavender-100 text-cj-800" : "text-gray-500 hover:bg-lavender-50 hover:text-navy-500"
            }`}
          >
            <span className={`shrink-0 ${active ? "text-cj-800" : "text-gray-400 group-hover:text-cj-800"}`}>
              <item.Icon className="w-[18px] h-[18px]" strokeWidth={1.9} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[13.5px] font-bold leading-tight">{item.label}</span>
              {item.sub && <span className="block text-[11px] text-gray-400 leading-tight truncate">{item.sub}</span>}
            </span>
            {item.badge ? (
              <span className="shrink-0 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {item.badge}
              </span>
            ) : premiumLocked ? (
              <Lock className="shrink-0 w-3.5 h-3.5 text-gray-300 group-hover:text-cj-800" aria-label="Premium feature" />
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
}

function HelpCard() {
  return (
    <div className="rounded-2xl bg-cj-900 p-4 text-center">
      <p className="text-[13px] font-bold text-white">Need Help?</p>
      <p className="text-[11px] text-white/60 mt-0.5 leading-snug">Talk to our Savings Team</p>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src="/images/cj-owl/owl-avatar-green.png" alt="" className="w-20 h-20 mx-auto my-2 object-contain select-none pointer-events-none" />
      <Link
        href="/my-account/concierge/"
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-[12.5px] font-bold py-2.5 transition-colors"
      >
        <Headset className="w-3.5 h-3.5" /> Message Consultant
      </Link>
      <a href="tel:+18446786868" className="mt-2 block text-center text-[11.5px] font-bold text-leaf-400 hover:text-leaf-300">
        +1-844-678-6868
      </a>
    </div>
  );
}

export function DashboardShell({
  children,
  memberName = "Member",
  memberTier = "Regular",
  memberAvatarUpdatedAt = null,
}: {
  children: React.ReactNode;
  memberName?: string;
  memberTier?: string;
  memberAvatarUpdatedAt?: string | null;
}) {
  const pathname = usePathname() ?? "";
  const crumbSeg = pathname.replace(/\/+$/, "").split("/").pop() || "";
  const isAccountHome = crumbSeg === "my-account" || crumbSeg === "";
  const crumbLabel = SECTION_LABELS[crumbSeg] ?? (crumbSeg ? crumbSeg[0].toUpperCase() + crumbSeg.slice(1) : "");
  const router = useRouter();
  const { locked, openPricing } = useMembershipGate();
  const [menuOpen, setMenuOpen] = React.useState(false);
  // Active-Premium == not locked (locked already accounts for tier + expiry), so an
  // expired-Premium member never sees both the VIP and the upgrade card.
  const isVip = !locked;
  // Per-page premium gate (client-side so it re-evaluates on every navigation — the
  // shared dashboard layout does not re-run on soft navigation). A Basic member on a
  // Premium-only route gets <FeatureLock> in place of the page content.
  const pageLocked = locked && isPremiumOnly(pathname);

  React.useEffect(() => { setMenuOpen(false); }, [pathname]);

  async function logout() {
    await fetch("/api/member/logout", { method: "POST" }).catch(() => {});
    router.push("/sign-in/");
    router.refresh();
  }

  return (
    <div className="min-h-screen bg-[#F5F7FC]">
      {/* ── Desktop sidebar ─────────────────────────────────────── */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-[260px] bg-white border-r border-gray-100 z-30">
        <div className="h-[68px] flex items-center px-5 border-b border-gray-100 shrink-0">
          <Logo />
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-4">
          <NavList pathname={pathname} locked={locked} />
        </div>
        <div className="p-3 space-y-3 border-t border-gray-100">
          {locked && (
            <button
              onClick={openPricing}
              className="w-full text-left rounded-2xl bg-gradient-to-br from-leaf-50 to-cj-50 border border-leaf-200 p-4"
            >
              <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-cj-800">
                <Lock className="w-3.5 h-3.5" /> Basic Plan
              </p>
              <p className="text-[12.5px] font-bold text-navy-500 mt-1">Upgrade to Premium</p>
              <p className="text-[11px] text-gray-500 mt-0.5">Unlock Savings, Renewals, Rewards & your full Savings Team.</p>
              <span className="mt-2 inline-block text-[11.5px] font-bold text-leaf-700">View plans →</span>
            </button>
          )}
          {isVip && (
            <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-leaf-50 border border-amber-200 p-4">
              <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-600">
                <Crown className="w-3.5 h-3.5" /> CJ Owl™ VIP
              </p>
              <p className="text-[13px] font-bold text-navy-500 mt-1">Gold Member</p>
              <p className="text-[11px] text-gray-500 mt-0.5">Member since June 2026</p>
              <Link href="/my-account/rewards/" className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-bold text-cj-800 hover:underline">
                View Benefits <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
          <HelpCard />
        </div>
      </aside>

      {/* Mobile: the full menu lives on the /my-account/menu/ screen (opened from the
          top-bar avatar) — no hamburger drawer. Desktop keeps the sidebar above. */}

      {/* ── Main column ─────────────────────────────────────────── */}
      <div className="lg:pl-[260px]">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-gray-100">
          <div className="h-[60px] lg:h-[68px] flex items-center gap-3 px-4 lg:px-8">
            <div className="lg:hidden"><Logo compact /></div>

            {/* Search (desktop) */}
            <div className="hidden lg:flex items-center flex-1 max-w-md">
              <div className="flex items-center gap-2 w-full h-10 px-3.5 rounded-xl bg-lavender-50 border border-lavender-200 text-gray-400">
                <Search className="w-4 h-4 shrink-0" />
                <input
                  placeholder="Search services, savings, documents…"
                  className="bg-transparent text-[13px] text-navy-500 placeholder:text-gray-400 outline-none w-full"
                />
              </div>
            </div>

            <div className="flex-1" />

            {/* Rewards */}
            <Link href="/my-account/rewards/" aria-label="Rewards" className="hidden lg:inline-flex p-2 rounded-lg text-gray-400 hover:text-cj-800 hover:bg-lavender-50">
              <Gift className="w-5 h-5" />
            </Link>

            {/* Notifications */}
            <NotificationBell />

            {/* Member menu — mobile: tap avatar → full Menu screen; desktop: dropdown */}
            <Link href="/my-account/menu/" aria-label="Menu" className="lg:hidden">
              <TopAvatar name={memberName} avatarUpdatedAt={memberAvatarUpdatedAt} />
            </Link>
            <div className="relative hidden lg:block">
              <button onClick={() => setMenuOpen((v) => !v)} className="flex items-center gap-2.5 pl-1.5 pr-2 py-1 rounded-xl hover:bg-lavender-50">
                <TopAvatar name={memberName} avatarUpdatedAt={memberAvatarUpdatedAt} />
                <span className="text-left leading-tight">
                  <span className="block text-[13px] font-bold text-navy-500 max-w-[140px] truncate">{memberName}</span>
                  <span className="block text-[11px] text-gray-400">CJ Owl™ {isVip ? "VIP" : "Member"}</span>
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl border border-gray-100 shadow-card-hover py-1.5 z-50">
                  <Link href="/my-account/profile/" className="block px-4 py-2 text-[13px] text-gray-600 hover:bg-lavender-50">Account details</Link>
                  <Link href="/my-account/settings/" className="block px-4 py-2 text-[13px] text-gray-600 hover:bg-lavender-50">Settings</Link>
                  <a href="/" className="flex items-center gap-2 px-4 py-2 text-[13px] text-gray-600 hover:bg-lavender-50">
                    <Globe className="w-3.5 h-3.5" /> Back to website
                  </a>
                  <div className="h-px bg-gray-100 my-1" />
                  <button onClick={logout} className="w-full text-left flex items-center gap-2 px-4 py-2 text-[13px] text-red-600 hover:bg-red-50">
                    <LogOut className="w-3.5 h-3.5" /> Log out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className={`px-4 lg:px-8 py-6 lg:py-8 pb-24 lg:pb-10 mx-auto ${isAccountHome ? "max-w-[1480px]" : "max-w-[1180px]"}`}>
          {!isAccountHome && (
            <nav aria-label="Breadcrumb" className="mb-5 flex items-center gap-2 text-[13px]">
              <Link
                href="/my-account/"
                className="inline-flex items-center gap-1.5 text-gray-500 hover:text-cj-800 font-semibold transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Dashboard
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
              <span className="font-bold text-navy-500">{crumbLabel}</span>
            </nav>
          )}
          {pageLocked ? <FeatureLock label={premiumFeatureLabel(pathname)} /> : children}
        </main>
      </div>

      {/* ── Mobile bottom tab bar (5 tabs; centered Ask CJ OWL owl) ─ */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-100 h-16 flex items-stretch px-1">
        {/* Left: Home, Savings */}
        <div className="flex-1 flex items-stretch justify-around">
          {BOTTOM.slice(0, 2).map((t) => <BottomTab key={t.href} {...t} locked={locked} active={isActive(pathname, t.href)} />)}
        </div>
        {/* Center: Ask CJ OWL — the owl mascot, slightly raised. The full menu
            (Profile, Membership, Vault…) stays reachable from the top-bar hamburger. */}
        <Link href="/my-account/ask/" className="relative -mt-4 shrink-0 flex flex-col items-center justify-center px-2">
          <span
            className={`w-12 h-12 rounded-full bg-white overflow-hidden flex items-center justify-center ring-2 shadow-[0_4px_14px_rgba(22,60,126,0.18)] ${
              isActive(pathname, "/my-account/ask/") ? "ring-cj-800" : "ring-lavender-200"
            }`}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/images/cj-owl/owl-avatar-green.png" alt="Ask CJ OWL" className="w-full h-full object-cover" />
          </span>
          <span className={`text-[10px] font-semibold mt-0.5 ${isActive(pathname, "/my-account/ask/") ? "text-cj-800" : "text-gray-400"}`}>
            Ask CJ OWL
          </span>
        </Link>
        {/* Right: Protection, Organize */}
        <div className="flex-1 flex items-stretch justify-around">
          {BOTTOM.slice(2).map((t) => <BottomTab key={t.href} {...t} locked={locked} active={isActive(pathname, t.href)} />)}
        </div>
      </nav>
    </div>
  );
}

function BottomTab({ label, href, Icon, active, premium, locked }: NavItem & { active: boolean; locked?: boolean }) {
  const showLock = locked && premium;
  return (
    <Link href={href} className={`relative flex flex-col items-center justify-center gap-0.5 px-3 ${active ? "text-cj-800" : "text-gray-400"}`}>
      <span className="relative">
        <Icon className="w-5 h-5" strokeWidth={active ? 2.4 : 1.9} />
        {showLock && <Lock className="absolute -top-1.5 -right-2 w-3 h-3 text-gray-300" aria-label="Premium" />}
      </span>
      <span className="text-[10px] font-semibold">{label}</span>
    </Link>
  );
}

/** Topbar member avatar — the uploaded photo (cache-busted by avatarUpdatedAt) or initials. */
function TopAvatar({ name, avatarUpdatedAt }: { name: string; avatarUpdatedAt: string | null }) {
  const [ok, setOk] = React.useState(!!avatarUpdatedAt);
  if (avatarUpdatedAt && ok) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={`/api/member/avatar?v=${encodeURIComponent(avatarUpdatedAt)}`}
        alt=""
        className="w-9 h-9 rounded-full object-cover shrink-0"
        onError={() => setOk(false)}
      />
    );
  }
  return (
    <span className="w-9 h-9 rounded-full bg-cj-900 text-white text-[12px] font-bold flex items-center justify-center shrink-0">
      {initialsOf(name)}
    </span>
  );
}
